export const THEME_STORAGE_KEY = "focusmap.theme" as const;
export const THEME_MODE_STORAGE_KEY = "focusmap.themeMode" as const;

// Theme modes: light, dark, or auto
export const THEME_MODES = [
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" },
  { label: "Auto", value: "auto" },
] as const;

export type ThemeMode = (typeof THEME_MODES)[number]["value"];

// Theme styles/palettes
export const THEME_STYLES = [
  { label: "FocusMap Classic", value: "classic" },
  { label: "FocusMap Vision 2026 (Nordic Dust)", value: "vision-2026" },
] as const;

export type ThemeStyle = (typeof THEME_STYLES)[number]["value"];

export const isThemeMode = (value: any): value is ThemeMode => {
  return THEME_MODES.some((t) => t.value === value);
};

export const isThemeStyle = (value: any): value is ThemeStyle => {
  return THEME_STYLES.some((t) => t.value === value);
};

export const getSystemPreferredMode = (): "light" | "dark" => {
  if (typeof window === "undefined") return "light";

  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
};

export const getSavedThemeMode = (): ThemeMode => {
  try {
    const value = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    return isThemeMode(value) ? value : "auto";
  } catch {
    return "auto";
  }
};

export const getSavedThemeStyle = (): ThemeStyle => {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeStyle(value) ? value : "classic";
  } catch {
    return "classic";
  }
};

export const setSavedThemeMode = (mode: ThemeMode) => {
  try {
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
};

export const setSavedThemeStyle = (style: ThemeStyle) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, style);
  } catch {
    // ignore
  }
};

export const resolveThemeMode = (mode: ThemeMode): "light" | "dark" => {
  if (mode === "auto") {
    return getSystemPreferredMode();
  }
  return mode;
};

export const buildThemeFileName = (
  style: ThemeStyle,
  mode: "light" | "dark",
): string => {
  // For classic style, use the original file names
  if (style === "classic") {
    return mode === "light" ? "focusmap-light" : "focusmap-dark";
  }
  // For other styles, use style-mode format (e.g., vision-2026-light)
  return `focusmap-${style}-${mode}`;
};

export const applyTheme = (style: ThemeStyle, mode: ThemeMode) => {
  if (typeof document === "undefined") return;

  const resolvedMode = resolveThemeMode(mode);
  const themeFile = buildThemeFileName(style, resolvedMode);

  const id = "focusmap-theme-link";
  let link = document.getElementById(id) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }

  // Keep it simple: theme files live in /public/themes
  link.href = `/themes/${themeFile}.css`;

  // Helpful for theme-specific selector scoping later
  document.documentElement.setAttribute("data-focusmap-theme", themeFile);
  document.documentElement.setAttribute("data-focusmap-mode", resolvedMode);
  document.documentElement.setAttribute("data-focusmap-style", style);
};

// Listen to system theme changes when in auto mode
export const setupThemeListener = (onThemeChange: () => void) => {
  if (typeof window === "undefined") return () => {};

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  const handler = () => {
    const savedMode = getSavedThemeMode();
    if (savedMode === "auto") {
      onThemeChange();
    }
  };

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }
  // Older browsers
  else if (mediaQuery.addListener) {
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }

  return () => {};
};

// Legacy compatibility helpers
export type FocusMapTheme =
  | "auto"
  | "focusmap-light"
  | "focusmap-dark"
  | "focusmap-vision-2026";

export const isFocusMapTheme = (value: any): value is FocusMapTheme => {
  const legacyThemes = [
    "auto",
    "focusmap-light",
    "focusmap-dark",
    "focusmap-vision-2026",
  ];
  return legacyThemes.includes(value);
};

export const getSavedTheme = (): FocusMapTheme => {
  // For backward compatibility, try to construct from new system
  const mode = getSavedThemeMode();
  const style = getSavedThemeStyle();

  if (mode === "auto") return "auto";
  if (style === "vision-2026") return "focusmap-vision-2026";
  return mode === "light" ? "focusmap-light" : "focusmap-dark";
};

export const setSavedTheme = (theme: FocusMapTheme) => {
  // Convert legacy theme to new system
  if (theme === "auto") {
    setSavedThemeMode("auto");
  } else if (theme === "focusmap-vision-2026") {
    setSavedThemeStyle("vision-2026");
    setSavedThemeMode("light"); // Vision 2026 is light by default
  } else if (theme === "focusmap-light") {
    setSavedThemeStyle("classic");
    setSavedThemeMode("light");
  } else if (theme === "focusmap-dark") {
    setSavedThemeStyle("classic");
    setSavedThemeMode("dark");
  }
};

export const THEMES = [
  { label: "Auto (System Preference)", value: "auto" as const },
  { label: "FocusMap Light", value: "focusmap-light" as const },
  { label: "FocusMap Dark", value: "focusmap-dark" as const },
  {
    label: "FocusMap Vision 2026 (Nordic Dust)",
    value: "focusmap-vision-2026" as const,
  },
];
